/* eslint @next/next/no-img-element: 0 */

import { useEffect, useState, useRef } from "react"
import rpc from "@/src/rpc"
import FileUploader from "./FileUploader"
import LoadingSpinner from "./LoadingSpinner"
import Modal from "./Modal"
import TrashIcon from "./icons/TrashIcon"
import CloseIcon from "./icons/CloseIcon"
import SearchIcon from "./icons/legacy/SearchIcon"
import UploadIcon from "./icons/UploadIcon"
import { sendMessage } from "@/src/useMessage"


export default function Gallery ({org, pickMode, onPick}) {
  const [assets, setAssets] = useState(null)
  const [query, setQuery] = useState('')
  const [editingAsset, setEditingAsset] = useState(null)
  const [editDescription, setEditDescription] = useState('')
  const [editSearchTerms, setEditSearchTerms] = useState('')
  const searchTimer = useRef(null)
  const uploaderMethods = useRef({})

  useEffect(() => {
    loadAssets()
  }, [org?.id])

  useEffect(() => {
    if (!assets) return
    const hasPending = assets.some(a => a.status === 'pending' || a.status === 'processing')
    if (!hasPending) return
    const timer = setTimeout(loadAssets, 3000)
    return () => clearTimeout(timer)
  }, [assets])

  async function loadAssets () {
    const result = await rpc('gallery.all', {})
    setAssets(result)
  }

  function handleSearchInput (value) {
    setQuery(value)
    clearTimeout(searchTimer.current)
    searchTimer.current = setTimeout(async () => {
      if (!value.trim()) {
        loadAssets()
      } else {
        const result = await rpc('gallery.search', {query: value})
        setAssets(result)
      }
    }, 300)
  }

  async function handleUpload (files) {
    for (const file of files) {
      try {
        const asset = await rpc('gallery.create', {fileId: file.id})
        setAssets(prev => [asset, ...(prev || [])])
      } catch (err) {
        sendMessage('error', 'Failed to add image to gallery.')
      }
    }
  }

  async function handleDelete (asset) {
    if (!confirm('Remove this image from your gallery?')) return
    try {
      await rpc('gallery.remove', {id: asset.id})
      setAssets(prev => prev.filter(a => a.id !== asset.id))
    } catch (err) {
      sendMessage('error', 'Failed to remove image.')
    }
  }

  function startEditing (asset) {
    setEditingAsset(asset)
    setEditDescription(asset.description || '')
    setEditSearchTerms(asset.searchTerms || '')
  }

  async function saveEditing () {
    if (!editingAsset) return
    try {
      const updated = await rpc('gallery.update', {
        id: editingAsset.id,
        description: editDescription,
        searchTerms: editSearchTerms,
      })
      setAssets(prev => prev.map(a => a.id === editingAsset.id ? updated : a))
      setEditingAsset(null)
    } catch (err) {
      sendMessage('error', 'Failed to update image.')
    }
  }

  function handleAssetClick (asset) {
    if (pickMode) {
      onPick(asset)
    } else {
      startEditing(asset)
    }
  }

  if (assets === null) return <LoadingSpinner/>

  return <>
    <section className="settings p-4 pb-6 flex-col gap-4 bg-f3f3f3 min-height-110">

      {/* Search */}
      <div className="flex-row gap-2">
        <div className="flex-row flex-grow relative">
          <span className="absolute left top p-2 opacity-50">
            <SearchIcon/>
          </span>
          <input
            className="input full-width pl-8"
            type="text"
            placeholder="Search images..."
            value={query}
            onChange={e => handleSearchInput(e.target.value)}
          />
        </div>
      </div>

      {/* Upload */}
      { !pickMode &&
        <FileUploader onUpload={handleUpload} multiple methods={uploaderMethods.current}>
          <div className="border-dashed border-gray rounded-lg p-4 text-center pointer hover_bg-white transition" onClick={() => uploaderMethods.current.open?.()}>
            <div className="flex-row flex-center gap-2 gray text-sm">
              <UploadIcon/>
              <span>Drop images here or click to upload</span>
            </div>
          </div>
        </FileUploader>
      }

      {/* Grid */}
      { assets.length === 0 &&
        <div className="flex-col gap-4 p-2">
          <p className="gray">
            { query ? 'No images found matching your search.' : 'Your image gallery is empty. Upload some images to get started.' }
          </p>
        </div>
      }

      <div className="grid gap-3" style={{gridTemplateColumns: 'repeat(2, 1fr)'}}>
        { assets.map(asset => (
          <div key={asset.id} className="relative">
            <div
              className="rounded-lg overflow-hidden shadow-lg bg-white pointer hover_scale transition"
              onClick={() => handleAssetClick(asset)}
            >
              <img
                src={asset.thumbnailUrl || asset.url}
                alt={asset.description || ''}
                className="full-width"
                style={{height: 140, objectFit: 'contain', display: 'block', background: '#666'}}
              />

              { asset.status === 'pending' || asset.status === 'processing' ?
                <div className="absolute bottom left right px-2 pb-2 pt-6 text-xs" style={{background: 'linear-gradient(transparent, rgba(0,0,0,0.6))', color: 'white'}}>
                  Analyzing...
                </div>
              : asset.description &&
                <div className="absolute bottom left right px-2 pb-2 pt-1" style={{fontSize: '0.65rem', lineHeight: 1.3, background: 'rgba(0,0,0,0.6)', color: 'white', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', textAlign: 'left'}}>
                  {asset.description}
                </div>
              }
            </div>

            { !pickMode &&
              <button
                className="hidden sm_flex delete-btn flex-center border-none pointer"
                style={{
                  position: 'absolute', top: -8, right: -8,
                  width: 20, height: 20, borderRadius: '50%',
                  background: '#e53e3e', color: 'white',
                  padding: 0,
                  boxShadow: '0 1px 4px rgba(0,0,0,0.4)',
                }}
                onClick={e => { e.stopPropagation(); handleDelete(asset) }}
              >
                <span style={{transform: 'scale(0.55)', display: 'block', lineHeight: 0}}><CloseIcon/></span>
              </button>
            }
          </div>
        ))}
      </div>

      {/* Edit description modal */}
      <Modal visible={!!editingAsset} onClickOutside={() => setEditingAsset(null)}>
        { editingAsset &&
          <div className="p-5 flex-col gap-3" style={{width: 600, maxWidth: '95vw'}}>
            <h3 className="text-lg bold">Edit Image Details</h3>

            <div className="flex-col sm_flex-row gap-4 align-start">
              { editingAsset.thumbnailUrl &&
                <img
                  src={editingAsset.thumbnailUrl}
                  alt=""
                  className="rounded flex-no-shrink full-width"
                  style={{maxWidth: 180, height: 180, objectFit: 'contain', background: '#666'}}
                />
              }

              <div className="flex-col gap-3 flex-grow">
                <label className="flex-col gap-1">
                  <span className="text-sm bold">Description</span>
                  <textarea
                    className="input"
                    rows={3}
                    style={{height: 'auto', resize: 'vertical'}}
                    value={editDescription}
                    onChange={e => setEditDescription(e.target.value)}
                    placeholder="Describe this image..."
                  />
                </label>

                <label className="flex-col gap-1">
                  <span className="text-sm bold">Search Terms</span>
                  <input
                    className="input"
                    type="text"
                    value={editSearchTerms}
                    onChange={e => setEditSearchTerms(e.target.value)}
                    placeholder="e.g. team photo, conference, John Smith"
                  />
                </label>
              </div>
            </div>

            <div className="flex-row gap-2 spread">
              <button
                className="button is-danger"
                onClick={() => { setEditingAsset(null); handleDelete(editingAsset) }}
              >
                <TrashIcon/>
              </button>
              <div className="flex-row gap-2">
                <button className="button" onClick={() => setEditingAsset(null)}>Cancel</button>
                <button className="button is-primary" onClick={saveEditing}>Save</button>
              </div>
            </div>
          </div>
        }
      </Modal>
    </section>

    <style jsx>{`
      .delete-btn {
        opacity: 0;
        transition: opacity 100ms ease-out;
      }
      *:hover > .delete-btn {
        opacity: 1;
        transition: opacity 100ms ease-out;
      }
      .delete-btn:hover {
        background: #c53030 !important;
        box-shadow: 0 2px 6px rgba(0,0,0,0.5) !important;
      }
    `}</style>
  </>
}
